'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';


export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const isHome = pathname === '/' || pathname === '/home';
  const isProcess = pathname === '/process';

  const navLinks = [
    { href: '/home', label: 'Home', anchor: false },
    { href: '/process', label: 'About', anchor: false },
    { href: '/services', label: 'Services & Packages', anchor: false },
    { href: '/results', label: 'Results', anchor: false },
    { href: '/contact', label: 'Contact', anchor: false },
  ];

  return (
    <nav
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass-nav py-3' : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link href="/home" className="flex items-center gap-3 flex-shrink-0">
          <div className="relative w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
            <img
              src="/assets/bb-brand-logo.svg"
              alt="Betty Berkely Career Coaching Logo"
              className="w-full h-full"
            />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-fraunces text-sm sm:text-base font-bold tracking-tight" style={{ color: '#F5F0E8' }}>Betty Berkely</span>
            <span className="text-xs font-mono-custom tracking-widest uppercase hidden sm:block" style={{ color: 'rgba(201,168,76,0.7)' }}>Career Coaching</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-7">
          {navLinks?.map((link) => {
            const isActive = !link?.anchor && (
              (link?.href === '/home' && isHome) ||
              (link?.href === '/process' && isProcess) ||
              pathname === link?.href
            );
            return link?.anchor ? (
              <a
                key={link?.label}
                href={link?.href}
                className="text-sm font-medium transition-opacity duration-200 hover:opacity-100"
                style={{ color: '#F5F0E8', opacity: 0.6 }}
              >
                {link?.label}
              </a>
            ) : (
              <Link
                key={link?.label}
                href={link?.href}
                className="text-sm font-medium transition-opacity duration-200"
                style={{ color: '#F5F0E8', opacity: isActive ? 1 : 0.6 }}
              >
                {link?.label}
              </Link>
            );
          })}
        </div>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="/contact"
            className="cta-secondary text-sm font-medium"
          >
            Free Critique →
          </a>
          <a
            href="/packages"
            className="cta-gold px-5 py-2.5 rounded-sm text-sm font-semibold tracking-wide"
          >
            View Packages
          </a>
        </div>

        {/* Mobile: hamburger button */}
        <button
          className="lg:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 focus:outline-none"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          <span
            className={`block w-6 h-0.5 transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`}
            style={{ background: '#F5F0E8' }}
          />
          <span
            className={`block w-6 h-0.5 transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`}
            style={{ background: '#F5F0E8' }}
          />
          <span
            className={`block w-6 h-0.5 transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`}
            style={{ background: '#F5F0E8' }}
          />
        </button>
      </div>
      {/* Mobile menu dropdown */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}
        style={{ background: 'rgba(74,25,66,0.98)', backdropFilter: 'blur(12px)' }}
      >
        <div className="px-6 py-5 flex flex-col gap-1">
          {navLinks?.map((link) => {
            const isActive = !link?.anchor && (
              (link?.href === '/home' && isHome) ||
              (link?.href === '/process' && isProcess) ||
              pathname === link?.href
            );
            return link?.anchor ? (
              <a
                key={link?.label}
                href={link?.href}
                className="py-3 text-base font-medium border-b"
                style={{ color: '#F5F0E8', borderColor: 'rgba(255,255,255,0.07)' }}
                onClick={() => setMobileOpen(false)}
              >
                {link?.label}
              </a>
            ) : (
              <Link
                key={link?.label}
                href={link?.href}
                className="py-3 text-base font-medium border-b"
                style={{
                  color: isActive ? '#C9A84C' : '#F5F0E8',
                  borderColor: 'rgba(255,255,255,0.07)',
                }}
                onClick={() => setMobileOpen(false)}
              >
                {link?.label}
              </Link>
            );
          })}
          {/* Mobile CTAs */}
          <div className="flex flex-col gap-3 mt-4">
            <a
              href="/contact"
              className="cta-secondary text-sm font-medium text-center py-3"
              onClick={() => setMobileOpen(false)}
            >
              Free Critique →
            </a>
            <a
              href="/packages"
              className="cta-gold px-5 py-3 rounded-sm text-sm font-semibold tracking-wide text-center"
              onClick={() => setMobileOpen(false)}
            >
              View Packages
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}